import { getFileKey } from "@/lib/file-key";
import {
  deleteSessionRecord,
  type SessionRecord,
} from "@/lib/storage/session-record-store";
import { isUploadingPhase } from "@/lib/sessions/phase";
import { loadDraftFilesByKeys } from "@/lib/storage/draft-files-store";
import { warmThumbnailCache } from "@/lib/clip/clip-thumbnail";
import {
  deleteUploadManifest,
  loadUploadManifest,
  type UploadManifest,
} from "@/lib/storage/upload-manifest-store";

function matchManifestFiles(manifest: UploadManifest, draftFiles: File[]) {
  const draftByKey = new Map(draftFiles.map((file) => [getFileKey(file), file]));
  const matched = manifest.files
    .map((manifestFile) => draftByKey.get(manifestFile.clientFileKey))
    .filter((file): file is File => file !== undefined);

  if (matched.length === 0 || matched.length !== manifest.files.length) {
    return null;
  }

  return matched;
}

export async function loadUploadSessionFiles(
  clientSessionId: string,
  manifest?: UploadManifest | null,
) {
  const resolvedManifest =
    manifest !== undefined ? manifest : await loadUploadManifest(clientSessionId);

  // Without a manifest the session has no defined file set. Draft blobs are
  // stored globally (keyed by file, not by session), so falling back to every
  // saved draft would attach unrelated files; treat it as unresumable instead.
  if (!resolvedManifest) return null;

  const keys = resolvedManifest.files.map((file) => file.clientFileKey);
  void warmThumbnailCache(keys);
  const draftFiles = await loadDraftFilesByKeys(keys);
  return matchManifestFiles(resolvedManifest, draftFiles);
}

export async function isUploadSessionResumable(clientSessionId: string) {
  const manifest = await loadUploadManifest(clientSessionId);
  if (!manifest) return false;
  const files = await loadUploadSessionFiles(clientSessionId, manifest);
  return files !== null;
}

export async function discardUnresumableUploadSession(clientSessionId: string) {
  await Promise.all([
    deleteSessionRecord(clientSessionId),
    deleteUploadManifest(clientSessionId),
  ]);
}

export async function filterResumableSessionRecords(records: SessionRecord[]) {
  const kept: SessionRecord[] = [];

  for (const session of records) {
    if (!isUploadingPhase(session.phase)) {
      kept.push(session);
      continue;
    }

    if (await isUploadSessionResumable(session.clientSessionId)) {
      kept.push(session);
      continue;
    }

    // Keep active uploads visible even before draft files finish persisting.
    // Resume attempts discard unresumable sessions explicitly in openSession.
    kept.push(session);
  }

  return kept;
}

